// src/components/pages/HomePage.js

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Box, Typography, CircularProgress } from "@mui/material";
import { useTheme } from "styled-components";
import {
  fetchUserProfileFromFirestore,
  fetchCurrentUserProfile,
  fetchUserChallenges,
} from "../../services/profileService";
import { fetchHomePageChallenges } from "../../services/challengeService";
import { fetchChallengeSubmissions } from "../../services/submissionService";

import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import { db } from "../../firebase";

import HomePageUserHeader from "../atoms/HomePageUserHeader";
import HomePageHeader from "../atoms/HomePageHeader";
import ProfileChallengesSection from "../organisms/Profile/ProfileChallengesSection";
import HomePageChallengesSection from "../organisms/HomePage/HomePageChallengesSection";
import HomePageSubmissionsSection from "../organisms/HomePage/HomePageSubmissionsSection";
import HomePageEventSpotlight from "../organisms/HomePage/HomePageEventSpotlight";

const HomePage = () => {
  const { profileId } = useParams();
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [latestChallenges, setLatestChallenges] = useState([]);
  const [latestSubmissions, setLatestSubmissions] = useState([]);
  const [error, setError] = useState(null);

  // Available event spotlight
  const [availableEvent, setAvailableEvent] = useState(null);
  const [availableThumb, setAvailableThumb] = useState(null);

  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    const fetchProfilesAndChallenges = async () => {
      setLoading(true);
      try {
        // Latest challenges and submissions
        const fetchedLatestChallenges = await fetchHomePageChallenges();
        setLatestChallenges(fetchedLatestChallenges.slice(0, 3));

        const fetchedLatestSubmissions = await fetchChallengeSubmissions();
        setLatestSubmissions(fetchedLatestSubmissions.slice(0, 3));

        // Current user
        const loggedInUser = await fetchCurrentUserProfile();
        setCurrentUser(loggedInUser);

        // Ensure local profile has uid if missing
        const storedUserProfile = JSON.parse(localStorage.getItem("userProfile"));
        if (storedUserProfile && !storedUserProfile.uid && loggedInUser?.uid) {
          storedUserProfile.uid = loggedInUser.uid;
          localStorage.setItem("userProfile", JSON.stringify(storedUserProfile));
        }

        // Profile to show
        const userToFetch = profileId || loggedInUser?.uid;
        if (userToFetch) {
          const fetchedProfile = await fetchUserProfileFromFirestore(userToFetch);
          setUserProfile(fetchedProfile);

          const fetchedChallenges = await fetchUserChallenges(
            userToFetch,
            loggedInUser?.uid
          );
          setChallenges(fetchedChallenges.slice(0, 3));
        }

        // Available event spotlight
        await fetchAvailableEventWithThumb();
      } catch (err) {
        console.error(err);
        setError("Error fetching profile or challenges data.");
      } finally {
        setLoading(false);
      }
    };

    const fetchAvailableEventWithThumb = async () => {
      const now = new Date();

      // Find events with endDate >= now, soonest ending first
      const eventsRef = collection(db, "events");
      const q = query(
        eventsRef,
        where("endDate", ">=", now),
        orderBy("endDate", "asc"),
        limit(5)
      );

      const snap = await getDocs(q);
      if (snap.empty) return;

      const toDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);

      const candidates = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((ev) => {
          const start = toDate(ev.startDate);
          const end = toDate(ev.endDate);
          return start && end && start <= now && end >= now;
        });

      if (candidates.length === 0) return;

      const ev = candidates[0];
      setAvailableEvent(ev);

      // Try to fetch a thumbnail from the event's content subcollection
      try {
        const contentSnap = await getDocs(collection(db, "events", ev.id, "content"));
        if (!contentSnap.empty) {
          // Prefer manual asset thumbnail; else first challenge thumb
          let thumb = null;
          for (const docSnap of contentSnap.docs) {
            const item = docSnap.data();
            if (item?.thumbnail?.url) {
              thumb = item.thumbnail.url;
              break;
            }
          }
          if (!thumb) {
            for (const docSnap of contentSnap.docs) {
              const item = docSnap.data();
              if (item?.submissionSaleId) {
                thumb = `https://swayer.blob.core.windows.net/${item.submissionSaleId}-pub/_thumb_large.jpg`;
                break;
              }
            }
          }
          if (thumb) setAvailableThumb(thumb);
        }
      } catch (e) {
        console.warn("Could not fetch event content thumbnail:", e?.message);
      }
    };

    fetchProfilesAndChallenges();
  }, [profileId, navigate]);

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "60vh",
          }}
        >
          <CircularProgress sx={{ color: theme.colors.red }} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Typography variant="h6" color="error">
            {error}
          </Typography>
        </Box>
      </Container>
    );
  }

  const cleanedDisplayName = (userProfile?.displayName || "User")
    .trim()
    .replace(/\s+/g, " ");
  const firstName = cleanedDisplayName.split(" ")[0];

  // UPDATED: navigate to /challenge/events/:eventId
  const goToEvent = () => {
    if (!availableEvent) return;
    navigate(`/challenge/events/${availableEvent.id}`, { state: { event: availableEvent } });
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4 }}>
      <Box>
        {/* Event Spotlight (top of page) */}
        {availableEvent && (
          <Box sx={{ mb: 6 }}>
            <HomePageEventSpotlight
              theme={theme}
              event={availableEvent}
              thumbnailUrl={availableThumb}
              onAction={goToEvent}
              ctaLabel="View & rent"
            />
          </Box>
        )}

        {/* Existing header + sections */}
        {!currentUser ? (
          <HomePageHeader />
        ) : (
          <>
            <HomePageUserHeader currentUser={currentUser} />
            <ProfileChallengesSection
              firstName={firstName}
              challenges={challenges}
              profileId={currentUser?.uid}
              theme={theme}
            />
          </>
        )}

        {latestChallenges.length > 0 && (
          <HomePageChallengesSection
            header="Latest Challenges"
            challenges={latestChallenges}
            link={`/challenge/all`}
            theme={theme}
          />
        )}

        {latestSubmissions.length > 0 && (
          <HomePageSubmissionsSection
            header="Latest Submissions"
            submissions={latestSubmissions}
            link={`/challenge/submissions/all`}
            theme={theme}
          />
        )}
      </Box>
    </Container>
  );
};

export default HomePage;
